/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { Fragment, useState } from 'react';

import type { VivaExam_ExamResultSummary as VivaExamResultSummaryType } from '__generated__/graphql-types-do-not-use';

import {
  Accordion,
  AccordionGroup,
  AccordionHeader,
  AccordionPanel,
  Button,
  ContextualHelp,
  InlineNotification,
  TemporaryDrawer,
  Typography2,
} from '@coursera/cds-core';
import { ArrowPreviousIcon, ExternalLinkIcon } from '@coursera/cds-icons';

import type { VivaExamQuestionsType } from 'bundles/academic-integrity/components/viva-exam/VivaExamSubmission';
import IntegrityCheckChat from 'bundles/academic-integrity/components/viva-exam/integrity-check-chat/IntegrityCheckChat';

import _t from 'i18n!nls/academic-integrity';

const styles = {
  nonExpandableSection: css`
    border-radius: 4px;
    border: 1px solid var(--neutral-stroke-primary, #929599);
    padding: 16px 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 2px 2px 12px;
  `,
  hideCloseButton: css`
    .cds-temporaryDrawerHeadingGroup-toolbarEndCol {
      display: none;
    }
  `,
  commentSpacing: css`
    display: flex;
    margin-top: 16px;
  `,
  aiQuestionTitle: css`
    color: var(--cds-color-neutral-primary-weak);
    margin-right: 12px;
  `,
  questionSpacing: css`
    margin-bottom: 16px;
  `,
  responseTitleSpacing: css`
    color: var(--cds-color-neutral-primary-weak);
    margin-bottom: 8px;
  `,
  panel: css`
    display: flex;
    flex-direction: column;
  `,
  responseSpacing: css`
    margin-bottom: 8px;
  `,
  contentSpacing: css`
    margin-left: 32px;
  `,
  scoreDisplay: css`
    white-space: nowrap;
  `,
  questionContainer: css`
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    gap: 8px;
  `,
  questionTitleContainer: css`
    margin-top: 24px;
    margin-bottom: 8px;
  `,
};

type IntegrityCheckAnalysisProps = {
  onBackButtonClick: () => void;
  open: boolean;
  vivaExamQuestions?: VivaExamQuestionsType;
  vivaExamResultConclusion?: VivaExamResultSummaryType['conclusion'];
  submitterName?: string;
  hasErrorFromLatestSubmissionData?: boolean;
};

const { HeadingGroup, Content } = TemporaryDrawer;

const IntegrityCheckAnalysis: React.FC<IntegrityCheckAnalysisProps> = ({
  onBackButtonClick,
  open,
  vivaExamQuestions,
  vivaExamResultConclusion,
  submitterName,
  hasErrorFromLatestSubmissionData,
}) => {
  // Extract the conclusion data from vivaExamResultSummary
  const {
    overallScore,
    knowledgeScore,
    knowledgeComment,
    knowledgeQuestions,
    consistencyScore,
    consistencyComment,
    consistencyQuestions,
    relevanceScore,
    relevanceComment,
    relevanceQuestions,
    understandingScore,
    understandingComment,
    understandingQuestions,
  } = vivaExamResultConclusion || {};

  // Map the scores and comments to the corresponding categories
  const categoriesData = [
    { score: consistencyScore, comment: consistencyComment, questions: consistencyQuestions, title: _t('Consistency') },
    {
      score: understandingScore,
      comment: understandingComment,
      questions: understandingQuestions,
      title: _t('Depth of understanding'),
    },
    { score: relevanceScore, comment: relevanceComment, questions: relevanceQuestions, title: _t('Relevance') },
    {
      score: knowledgeScore,
      comment: knowledgeComment,
      questions: knowledgeQuestions,
      title: _t('Knowledge of the submission'),
    },
  ];
  const [showIntegrityCheckSubmission, setShowIntegrityCheckSubmission] = useState(false);
  const totalQuestionsCount = vivaExamQuestions?.examSession.totalExpectedQuestions;

  const getFirstName = (fullName?: string) => {
    if (!fullName) {
      return 'Learner';
    }
    return fullName.includes(' ') ? fullName.split(' ')[0] : fullName;
  };

  if (showIntegrityCheckSubmission && totalQuestionsCount) {
    return (
      <IntegrityCheckChat
        onDismiss={() => setShowIntegrityCheckSubmission(false)}
        totalQuestionsCount={totalQuestionsCount}
        vivaExamQuestions={vivaExamQuestions}
        submitterFirstName={getFirstName(submitterName)}
      />
    );
  }

  return (
    <Fragment>
      <TemporaryDrawer
        open={open}
        onClose={onBackButtonClick}
        width="medium"
        anchor="right"
        sticky="none"
        css={styles.hideCloseButton}
      >
        <HeadingGroup
          backAction={
            <Button
              icon={<ArrowPreviousIcon size="medium" />}
              iconPosition="before"
              size="small"
              variant="ghost"
              onClick={onBackButtonClick}
              edgeAlign="start"
            >
              {_t('Back')}
            </Button>
          }
        >
          {_t('Integrity Check Analysis')}
        </HeadingGroup>
        <Content>
          {hasErrorFromLatestSubmissionData && (
            <InlineNotification severity="error">
              {_t('There was an issue with retrieving the learner’s full response. Please try again later.')}
            </InlineNotification>
          )}
          <div css={styles.nonExpandableSection}>
            <Typography2 variant="subtitleMedium" component="span">
              {_t('Integrity Check score:')} {overallScore !== undefined ? `${overallScore} / 10` : '-- / 10'}
            </Typography2>
            <Button
              icon={<ExternalLinkIcon size="medium" />}
              iconPosition="after"
              size="small"
              variant="primary"
              onClick={() => setShowIntegrityCheckSubmission(true)}
            >
              {_t("View learner's full input")}
            </Button>
          </div>
          <AccordionGroup spacing={8}>
            {categoriesData.map((categoryData) => (
              <Accordion variant="standard" key={`category~${categoryData.title}`}>
                <AccordionHeader
                  label={categoryData.title}
                  supportText={
                    <Typography2 variant="bodyPrimary" component="span" css={styles.commentSpacing}>
                      {categoryData.comment}
                    </Typography2>
                  }
                >
                  <Typography2 variant="subtitleMedium" component="span" css={styles.scoreDisplay}>
                    {_t('Score:')} {categoryData.score !== undefined ? `${categoryData.score} / 10` : '--'}
                  </Typography2>
                </AccordionHeader>
                <AccordionPanel>
                  <div css={styles.contentSpacing}>
                    {categoryData.questions?.map((question, questionIndex) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <div key={`question~${questionIndex}`} css={styles.panel}>
                        <div css={styles.questionContainer}>
                          <ContextualHelp
                            helpToggleProps={{
                              'aria-label': _t('Why was this question asked?'),
                            }}
                            tooltipProps={{
                              closeButtonProps: {
                                'aria-label': _t('Close information about why this question was asked'),
                              },
                            }}
                            label={
                              <Fragment>
                                <Typography2 variant="subtitleMedium" component="span" css={styles.aiQuestionTitle}>
                                  {_t('AI Generated Question')}
                                  {(categoryData.questions?.length ?? 0) > 1 && ` ${questionIndex + 1}`}
                                </Typography2>
                                <Typography2 variant="bodySecondary" component="span">
                                  {_t('Why was this asked?')}
                                </Typography2>
                              </Fragment>
                            }
                            css={styles.questionTitleContainer}
                          >
                            <Typography2 variant="bodyPrimary" component="div">
                              {question.rationale}
                            </Typography2>
                          </ContextualHelp>
                        </div>

                        <Typography2 variant="bodyPrimary" component="span" css={styles.questionSpacing}>
                          {question.question}
                        </Typography2>
                        <Typography2 variant="subtitleMedium" component="span" css={styles.responseTitleSpacing}>
                          {_t("Learner's Response")}:
                        </Typography2>
                        <Typography2 variant="bodyPrimary" component="span" css={styles.responseSpacing}>
                          {question.response}
                        </Typography2>
                      </div>
                    ))}
                  </div>
                </AccordionPanel>
              </Accordion>
            ))}
          </AccordionGroup>
        </Content>
      </TemporaryDrawer>
    </Fragment>
  );
};

export default IntegrityCheckAnalysis;
